/**
 * First-party consent telemetry client for the landing site.
 *
 * Fires-and-forgets POSTs to our own backend at the URL configured via
 * GATSBY_CONSENT_TELEMETRY_URL (defaults to `/api/telemetry/consent` which
 * can be proxied by Cloudflare / nginx to deeplake-api). Failures are
 * swallowed, telemetry must never break the consent flow or block UI.
 *
 * Not CIPA-exposed: same-origin POST (or origin-controlled reverse proxy).
 */

export type ConsentTelemetryEvent =
  | "banner_shown"
  | "banner_accepted"
  | "banner_rejected"
  | "banner_dismissed"
  | "banner_customized"
  | "banner_revoked"

export type ConsentTelemetryPayload = {
  event: ConsentTelemetryEvent
  session_id: string
  analytics?: boolean
  marketing?: boolean
  path?: string
  geo?: "strict" | "relaxed" | "unknown"
  gpc?: boolean
}

const ENDPOINT = process.env.GATSBY_CONSENT_TELEMETRY_URL || "/api/telemetry/consent"

let cachedSessionId: string | null = null

const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr"
  if (cachedSessionId) return cachedSessionId
  try {
    const existing = window.sessionStorage.getItem("activeloop_telemetry_session")
    if (existing) {
      cachedSessionId = existing
      return existing
    }
  } catch {
    // sessionStorage unavailable
  }
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  const id = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
  try {
    window.sessionStorage.setItem("activeloop_telemetry_session", id)
  } catch {
    // ignore
  }
  cachedSessionId = id
  return id
}

export const emitConsentTelemetry = (
  event: ConsentTelemetryEvent,
  extra: Partial<ConsentTelemetryPayload> = {},
): void => {
  if (typeof window === "undefined") return
  // Disable in vitest only, Playwright / real browsers still emit.
  if (process.env.NODE_ENV === "test") return

  const payload: ConsentTelemetryPayload = {
    event,
    session_id: getSessionId(),
    path: window.location.pathname,
    ...extra,
  }

  try {
    const body = JSON.stringify(payload)
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([body], { type: "application/json" })
        const ok = navigator.sendBeacon(ENDPOINT, blob)
        if (ok) return
      } catch {
        // fall through
      }
    }
    Promise.resolve()
      .then(() =>
        fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }),
      )
      .catch(() => {
        // swallow
      })
  } catch {
    // swallow
  }
}
